import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {Search, X, ShieldCheck, Waves, MapPin, Briefcase} from 'lucide-react';
import { REGIONS } from "@/data/formations";

type Category = "" | "BNSSA" | "MNS";
type Contract =
  | ""
  | "CDI"
  | "CDD"
  | "CDD-saisonnier"
  | "interim"
  | "auto-entrepreneur"
  | "temps-partiel-CDI"
  | "temps-partiel-CDD"
  | "stage"
  | "contractuel"
  | "titulaire";

const CONTRACTS: { value: Contract; label: string }[] = [
  { value: "CDI", label: "CDI" },
  { value: "CDD", label: "CDD" },
  { value: "CDD-saisonnier", label: "Saisonnier" },
  { value: "interim", label: "Intérim" },
  { value: "auto-entrepreneur", label: "Auto-entrepreneur" },
  { value: "temps-partiel-CDI", label: "Temps partiel CDI" },
  { value: "temps-partiel-CDD", label: "Temps partiel CDD" },
  { value: "stage", label: "Stage" },
  { value: "contractuel", label: "Contractuel" },
  { value: "titulaire", label: "Titulaire" }
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function MobileSearchOverlay({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<Category>("");
  const [region, setRegion] = useState("");
  const [contract, setContract] = useState<Contract>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  // Lock body scroll + autofocus search field when overlay opens + remember opener for focus return
  useEffect(() => {
    if (!open) return;
    previousActiveElementRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 80);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(focusTimer);
      // Restore focus to the element that opened the overlay
      previousActiveElementRef.current?.focus?.();
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (category) params.set("categorie", category);
    if (region) params.set("lieu", region);
    if (contract) params.set("contrat", contract);
    const qs = params.toString();
    navigate(`/offres${qs ? `?${qs}` : ""}`);
    onClose();
    // Reset for next opening
    setQ("");
    setCategory("");
    setRegion("");
    setContract("");
  };

  const handleReset = () => {
    setQ("");
    setCategory("");
    setRegion("");
    setContract("");
    inputRef.current?.focus();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-white lg:hidden flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Recherche d'offres d'emploi">

      {/* Top bar : input + close */}
      <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-100 bg-white">
        <form onSubmit={handleSubmit} className="flex-1 flex items-center gap-2" role="search" aria-label="Rechercher des offres d'emploi">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" focusable="false" />
            <input
              ref={inputRef}
              id="mobile-search-input"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un poste, un lieu…"
              aria-label="Mots-clés de recherche"
              className="w-full h-11 pl-10 pr-9 rounded-xl bg-gray-50 border border-gray-200 text-[15px] focus:bg-white focus:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 transition-colors"
              enterKeyHint="search"
              autoComplete="off" />

            {q &&
            <button
              type="button"
              onClick={() => setQ("")}
              aria-label="Effacer la recherche"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900">

                <X className="w-4 h-4" aria-hidden="true" focusable="false" />
              </button>
            }
          </div>
        </form>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer la recherche"
          className="p-2 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900">

          <X className="w-5 h-5" aria-hidden="true" focusable="false" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-6">
        {/* Catégorie */}
        <div role="group" aria-labelledby="filter-category-label">
          <p id="filter-category-label" className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2.5">
            Catégorie
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCategory(category === "BNSSA" ? "" : "BNSSA")}
              aria-pressed={category === "BNSSA"}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[13.5px] font-semibold border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
              category === "BNSSA" ?
              "bg-red-600 text-white border-red-600" :
              "bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:bg-red-50/50"}`
              }>

              <ShieldCheck className="w-4 h-4" aria-hidden="true" focusable="false" />
              BNSSA
            </button>
            <button
              type="button"
              onClick={() => setCategory(category === "MNS" ? "" : "MNS")}
              aria-pressed={category === "MNS"}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[13.5px] font-semibold border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
              category === "MNS" ?
              "bg-blue-600 text-white border-blue-600" :
              "bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50/50"}`
              }>

              <Waves className="w-4 h-4" aria-hidden="true" focusable="false" />
              MNS
            </button>
          </div>
        </div>

        {/* Région */}
        <div role="group" aria-labelledby="filter-region-label">
          <p id="filter-region-label" className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2.5 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" aria-hidden="true" focusable="false" />
            Région
          </p>
          <div className="flex flex-wrap gap-2">
            {REGIONS.map((r) =>
            <button
              key={r}
              type="button"
              onClick={() => setRegion(region === r ? "" : r)}
              aria-pressed={region === r}
              className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${
              region === r ?
              "bg-gray-900 text-white border-gray-900" :
              "bg-white text-gray-700 border-gray-200 hover:border-gray-400"}`
              }>

                {r}
              </button>
            )}
          </div>
        </div>

        {/* Contrat */}
        <div role="group" aria-labelledby="filter-contract-label">
          <p id="filter-contract-label" className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2.5 flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5" aria-hidden="true" focusable="false" />
            Type de contrat
          </p>
          <div className="flex flex-wrap gap-2">
            {CONTRACTS.map((c) =>
            <button
              key={c.value}
              type="button"
              onClick={() => setContract(contract === c.value ? "" : c.value)}
              aria-pressed={contract === c.value}
              className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${
              contract === c.value ?
              "bg-gray-900 text-white border-gray-900" :
              "bg-white text-gray-700 border-gray-200 hover:border-gray-400"}`
              }>

                {c.label}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div
        className="border-t border-gray-100 bg-white px-4 py-3 flex items-center gap-2"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>

        <button
          type="button"
          onClick={handleReset}
          className="px-4 py-3 rounded-xl text-[13.5px] font-semibold text-gray-600 hover:bg-gray-50 border border-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2">

          Réinitialiser
        </button>
        <button
          type="button"
          onClick={() => handleSubmit()}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#222222] text-white px-4 py-3 rounded-xl text-[14px] font-semibold hover:bg-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2">

          <Search className="w-4 h-4" aria-hidden="true" focusable="false" />
          Voir les offres
        </button>
      </div>
    </div>);

}
